import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HandwrittenSignatureField } from './HandwrittenSignatureField';
import { useUserProfile } from '../context/UserProfileContext';
import { splitProfileSignature } from '../utils/userProfile';

const inputClass =
  'px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full';
const labelClass = 'block text-xs text-gray-400 mb-1';
const checkboxClass = 'rounded border-gray-600 bg-black text-amber-400 focus:ring-amber-400/40';

const WORKER_ROWS = Array.from({ length: 10 }, (_, i) => i);

/**
 * Creating a fall protection plan for your worksite — Fillable template
 * Content from public/creating-a-fall-protection-plan-for-your-worksite-fillable-template-pdf-en.pdf
 */
export function FallProtectionPlanForm() {
  const { name: profileName, signature: profileSignature } = useUserProfile();

  const [employerNames, setEmployerNames] = useState('');
  const [startDate, setStartDate] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [city, setCity] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [workArea, setWorkArea] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [firstAidAttendantName, setFirstAidAttendantName] = useState('');
  const [firstAidContact, setFirstAidContact] = useState('');
  const [firstAidLevel, setFirstAidLevel] = useState('');
  const [firstAidExpiry, setFirstAidExpiry] = useState('');

  const [maxHeightPeak, setMaxHeightPeak] = useState('');
  const [maxHeightEaves, setMaxHeightEaves] = useState('');
  const [maxWorkingHeight, setMaxWorkingHeight] = useState('');
  const [roofSlopes, setRoofSlopes] = useState('');
  const [toeHolds, setToeHolds] = useState('');
  const [toeHoldsDescribe, setToeHoldsDescribe] = useState('');
  const [proximityHighVoltage, setProximityHighVoltage] = useState('');
  const [proximityLowVoltage, setProximityLowVoltage] = useState('');
  const [accessTypeLocation, setAccessTypeLocation] = useState('');
  const [potentialHazards, setPotentialHazards] = useState('');

  const [areaDescription, setAreaDescription] = useState('');
  const [fpTypes, setFpTypes] = useState({
    guardrails: false,
    fallRestraint: false,
    fallArrest: false,
    other: false,
  });
  const [otherProceduresExplain, setOtherProceduresExplain] = useState('');
  const [reasonForSelection, setReasonForSelection] = useState('');
  const [mfrAvailableOnSite, setMfrAvailableOnSite] = useState(false);
  const [mfrReviewedWithWorkers, setMfrReviewedWithWorkers] = useState(false);
  const [equipmentProcedures, setEquipmentProcedures] = useState('');

  const [rescueProcedures, setRescueProcedures] = useState('');

  const [workers, setWorkers] = useState(() =>
    WORKER_ROWS.map(() => ({
      name: '',
      signature: { mode: 'type', text: '', image: '' },
      companyName: '',
    }))
  );

  const [supervisorName, setSupervisorName] = useState(profileName || '');
  const [supervisorSignature, setSupervisorSignature] = useState(() => {
    const split = splitProfileSignature(profileSignature);
    return { mode: split.image ? 'draw' : 'type', ...split };
  });
  const [supervisorContact, setSupervisorContact] = useState('');
  const [reviewDate, setReviewDate] = useState('');

  useEffect(() => {
    if (profileName) setSupervisorName((prev) => prev || profileName);
    if (profileSignature) {
      const split = splitProfileSignature(profileSignature);
      setSupervisorSignature((prev) => {
        if (prev.text || prev.image) return prev;
        return { mode: split.image ? 'draw' : 'type', ...split };
      });
    }
  }, [profileName, profileSignature]);

  const toggleFpType = (key) => {
    setFpTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateWorker = (index, patch) => {
    setWorkers((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  return (
    <form className="space-y-6 w-full">
      <div className="space-y-1">
        <p className="text-lg font-semibold text-white">Creating a fall protection plan</p>
        <p className="text-lg font-semibold text-white">for your worksite</p>
        <p className="text-sm text-gray-400">Fillable template</p>
      </div>

      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <p className="text-sm text-gray-300 leading-relaxed">
          Planning plays a key role in protecting workers from fall hazards. A system of fall
          protection is required when working at heights of 3 m (10 ft.) or more, or where a fall
          from a height less than 3 m (10 ft.) involves a risk of injury greater than that from the
          impact on a flat surface. In some cases, areas accessible to workers must have guards or
          guardrails. See section 4.55 of the Occupational Health and Safety Regulation.
        </p>
        <p className="text-sm text-gray-300 leading-relaxed">
          The fall protection plan template below is meant to help employers:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-300 ml-1">
          <li>Navigate the planning process</li>
          <li>Address site-specific conditions</li>
          <li>Demonstrate due diligence</li>
          <li>Instruct workers</li>
          <li>Choose fall protection systems</li>
        </ul>
        <p className="text-sm text-gray-300 leading-relaxed">
          For information on how to use this template, see the guide Creating a fall protection plan
          for your worksite: Guide to the fillable template.
        </p>
        <p className="text-sm text-gray-300 leading-relaxed">
          Your fall protection plan must be written down if any work will be done at heights of 7.5
          m (25 ft.) or more. There are other circumstances where written work procedures (for
          example, “first person up, last person down”) are required, as specified in section
          11.3(1)(b) of the Regulation.
        </p>
        <p className="text-sm text-gray-300 leading-relaxed">
          The fall protection plan needs to address site-specific conditions and be compliant with
          the Regulation. Your written plan must be available at the worksite before work at heights
          starts.
        </p>
        <p className="text-sm text-gray-300 leading-relaxed border border-gray-700 rounded-md p-3 bg-black/30">
          When using this template to develop your fall protection plan, attach additional pages as
          necessary. You may want to attach additional information, such as site inspection field
          notes, toolbox meeting notes, manufacturer’s instructions, specific work procedures, or a
          contact list.
        </p>
        <p className="text-sm text-gray-300 leading-relaxed">
          If you’re using this template as a fall protection plan for work at 7.5 m (25 ft.) or more,
          or if section 11.3(1)(b) of the Regulation applies, the information in the template is
          expected to be complete, at a minimum.
        </p>
        <p className="text-sm text-gray-300 leading-relaxed">
          Scan to find more information about fall protection at worksafebc.com/residential-construction.
        </p>
        <p className="text-[11px] text-gray-500">51E23 (21/06)</p>
      </div>

      {/* 1. Project site description */}
      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <h3 className="text-base font-semibold text-amber-200/90">1. Project site description</h3>
        <p className="text-sm text-gray-300">Describe specific details for your worksite.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Name of employer(s)</label>
            <input
              type="text"
              value={employerNames}
              onChange={(e) => setEmployerNames(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Start date for work at heights</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Site address</label>
            <input
              type="text"
              value={siteAddress}
              onChange={(e) => setSiteAddress(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Site description (type of structure):</label>
            <input
              type="text"
              value={siteDescription}
              onChange={(e) => setSiteDescription(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Work area</label>
          <input
            type="text"
            value={workArea}
            onChange={(e) => setWorkArea(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Task description</label>
          <textarea
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            rows={3}
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelClass}>Name of first aid attendant</label>
            <input
              type="text"
              value={firstAidAttendantName}
              onChange={(e) => setFirstAidAttendantName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Contact for first aid attendant</label>
            <input
              type="text"
              value={firstAidContact}
              onChange={(e) => setFirstAidContact(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Level of first aid attendant:</label>
            <input
              type="text"
              value={firstAidLevel}
              onChange={(e) => setFirstAidLevel(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Expiry date for first aid certificate:</label>
            <input
              type="date"
              value={firstAidExpiry}
              onChange={(e) => setFirstAidExpiry(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* 2. Site-specific details for work at heights */}
      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <h3 className="text-base font-semibold text-amber-200/90">
          2. Site-specific details for work at heights
        </h3>
        <p className="text-sm text-gray-300">
          Use the diagram on the next page to illustrate the following details, which are part of a
          fall protection plan, at a minimum.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Maximum height (peak)</label>
            <input
              type="text"
              value={maxHeightPeak}
              onChange={(e) => setMaxHeightPeak(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Maximum height (eaves)</label>
            <input
              type="text"
              value={maxHeightEaves}
              onChange={(e) => setMaxHeightEaves(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Maximum working height</label>
            <input
              type="text"
              value={maxWorkingHeight}
              onChange={(e) => setMaxWorkingHeight(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Roof slopes</label>
            <input
              type="text"
              value={roofSlopes}
              onChange={(e) => setRoofSlopes(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              Toe-holds (required for slopes of 8:12 or more)
            </label>
            <div className="flex items-center gap-4 h-[42px]">
              <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="radio"
                  name="toe-holds"
                  checked={toeHolds === 'yes'}
                  onChange={() => setToeHolds('yes')}
                  className={checkboxClass}
                />
                Yes
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="radio"
                  name="toe-holds"
                  checked={toeHolds === 'no'}
                  onChange={() => setToeHolds('no')}
                  className={checkboxClass}
                />
                No
              </label>
            </div>
          </div>
          <div>
            <label className={labelClass}>If yes, describe types of toe-holds used</label>
            <input
              type="text"
              value={toeHoldsDescribe}
              onChange={(e) => setToeHoldsDescribe(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Proximity to high-voltage power lines (if applicable)
            </label>
            <input
              type="text"
              value={proximityHighVoltage}
              onChange={(e) => setProximityHighVoltage(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              Proximity to low-voltage power lines (if applicable)
            </label>
            <input
              type="text"
              value={proximityLowVoltage}
              onChange={(e) => setProximityLowVoltage(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Access type and location</label>
          <textarea
            value={accessTypeLocation}
            onChange={(e) => setAccessTypeLocation(e.target.value)}
            rows={2}
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Potential hazards</label>
          <textarea
            value={potentialHazards}
            onChange={(e) => setPotentialHazards(e.target.value)}
            rows={3}
            className={inputClass}
          />
        </div>
      </div>

      {/* 3. Worksite details */}
      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <h3 className="text-base font-semibold text-amber-200/90">3. Worksite details</h3>
        <p className="text-sm text-gray-300">
          Use this blank grid to draw specific worksite details, such as anchor locations, building
          elevations, access points,and hazards at or near the work location. If you need more space
          for your drawing or you’re working in more than one area, print additional copies of this
          page, as necessary.
        </p>
        <WorksiteGridCanvas />
      </div>

      {/* 4. Type of fall protection system */}
      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <h3 className="text-base font-semibold text-amber-200/90">
          4. Type of fall protection system for each area and procedures for set-up, use, and removal
        </h3>
        <p className="text-sm text-gray-300 leading-relaxed">
          When choosing a fall protection system, follow the fall protection hierarchy (see section
          11.2 of the Regulation). Describe all the equipment you’ll be using for your fall
          protection systems. Describe your procedures for assembling, inspecting, using,
          maintaining, and removing the fall protection equipment. If you will be working in more
          than one area, print additional copies of this page as needed.
        </p>

        <div>
          <label className={labelClass}>Area description</label>
          <textarea
            value={areaDescription}
            onChange={(e) => setAreaDescription(e.target.value)}
            rows={2}
            className={inputClass}
          />
        </div>

        <div>
          <p className={labelClass}>Type of fall protection</p>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={fpTypes.guardrails}
                onChange={() => toggleFpType('guardrails')}
                className={checkboxClass}
              />
              Guardrails
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={fpTypes.fallRestraint}
                onChange={() => toggleFpType('fallRestraint')}
                className={checkboxClass}
              />
              Fall restraint
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={fpTypes.fallArrest}
                onChange={() => toggleFpType('fallArrest')}
                className={checkboxClass}
              />
              Fall arrest
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={fpTypes.other}
                onChange={() => toggleFpType('other')}
                className={checkboxClass}
              />
              Other procedures (explain)
            </label>
          </div>
          {fpTypes.other ? (
            <div className="mt-2">
              <textarea
                value={otherProceduresExplain}
                onChange={(e) => setOtherProceduresExplain(e.target.value)}
                rows={2}
                className={inputClass}
              />
            </div>
          ) : null}
        </div>

        <div>
          <label className={labelClass}>Reason for selection</label>
          <textarea
            value={reasonForSelection}
            onChange={(e) => setReasonForSelection(e.target.value)}
            rows={2}
            className={inputClass}
          />
        </div>

        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={mfrAvailableOnSite}
              onChange={(e) => setMfrAvailableOnSite(e.target.checked)}
              className={checkboxClass}
            />
            Manufacturer’s instructions are available on site.
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={mfrReviewedWithWorkers}
              onChange={(e) => setMfrReviewedWithWorkers(e.target.checked)}
              className={checkboxClass}
            />
            Manufacturer’s instructions have been reviewed with workers.
          </label>
        </div>

        <div>
          <label className={labelClass}>
            Equipment description and procedures for set-up, use, maintenance, inspection, and
            removal:
          </label>
          <textarea
            value={equipmentProcedures}
            onChange={(e) => setEquipmentProcedures(e.target.value)}
            rows={5}
            className={inputClass}
          />
        </div>
      </div>

      {/* 5. Rescue procedures */}
      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <h3 className="text-base font-semibold text-amber-200/90">5. Rescue procedures</h3>
        <p className="text-sm text-gray-300 leading-relaxed">
          Describe how you would rescue a fallen worker. Include any tools, equipment, or machinery
          you would use for the rescue. Specify who should be contacted to carry out the rescue, if
          applicable. (Attach additional pages, if necessary.)
        </p>
        <textarea
          value={rescueProcedures}
          onChange={(e) => setRescueProcedures(e.target.value)}
          rows={6}
          className={inputClass}
        />
      </div>

      {/* 6. Record of review */}
      <div className="border border-gray-800 rounded-lg overflow-hidden bg-gray-900/30 p-4 md:p-5 space-y-4">
        <h3 className="text-base font-semibold text-amber-200/90">6. Record of review</h3>
        <p className="text-sm text-gray-300 leading-relaxed">
          The site-specific fall protection plan has been reviewed with the following workers.
          Employers should keep this page for their records.
        </p>

        <div className="space-y-0">
          {workers.map((row, index) => (
            <div key={index} className="border-b border-gray-800 py-3 last:border-b-0">
              <div className="mb-2 flex items-start gap-2">
                <span className="w-6 shrink-0 pt-2 text-gray-500 tabular-nums">{index + 1}.</span>
                <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateWorker(index, { name: e.target.value })}
                    className={inputClass}
                    placeholder="Worker’s name"
                  />
                  <input
                    type="text"
                    value={row.companyName}
                    onChange={(e) => updateWorker(index, { companyName: e.target.value })}
                    className={inputClass}
                    placeholder="Company name"
                  />
                </div>
              </div>
              <div className="pl-8">
                <HandwrittenSignatureField
                  textValue={row.signature.text}
                  imageValue={row.signature.image}
                  onTextChange={(text) =>
                    updateWorker(index, { signature: { ...row.signature, text } })
                  }
                  onImageChange={(image) =>
                    updateWorker(index, { signature: { ...row.signature, image } })
                  }
                  placeholder="Type signature"
                  inputClassName={inputClass}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Supervisor’s name</label>
              <input
                type="text"
                value={supervisorName}
                onChange={(e) => setSupervisorName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Supervisor’s contact number</label>
              <input
                type="text"
                value={supervisorContact}
                onChange={(e) => setSupervisorContact(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div className="space-y-2 border-t border-gray-800 pt-4">
            <div className="flex items-center gap-2">
              <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Supervisor’s signature</p>
              <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
            </div>
            <HandwrittenSignatureField
              textValue={supervisorSignature.text}
              imageValue={supervisorSignature.image}
              onTextChange={(text) => setSupervisorSignature((prev) => ({ ...prev, text }))}
              onImageChange={(image) => setSupervisorSignature((prev) => ({ ...prev, image }))}
              dateValue={reviewDate}
              onDateChange={setReviewDate}
              placeholder="Type signature"
              inputClassName={inputClass}
            />
          </div>
        </div>
      </div>
    </form>
  );
}

function WorksiteGridCanvas() {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const paintGrid = useCallback((ctx, canvas) => {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    const step = 20;
    for (let x = 0; x <= canvas.width; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y <= canvas.height; y += step) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    paintGrid(ctx, canvas);
  }, [paintGrid]);

  const getPoint = (event, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (source.clientX - rect.left) * scaleX,
      y: (source.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (event) => {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawingRef.current = true;
    lastPointRef.current = getPoint(event, canvas);
  };

  const moveDraw = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const point = getPoint(event, canvas);
    const last = lastPointRef.current;
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    setIsEmpty(false);
  };

  const endDraw = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    paintGrid(ctx, canvas);
    setIsEmpty(true);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={900}
        height={520}
        className="w-full border border-gray-700 rounded bg-white touch-none cursor-crosshair"
        onMouseDown={startDraw}
        onMouseMove={moveDraw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={moveDraw}
        onTouchEnd={endDraw}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={clear}
          disabled={isEmpty}
          className="text-xs text-amber-200/80 hover:text-amber-200 underline disabled:opacity-40 disabled:no-underline"
        >
          Clear drawing
        </button>
      </div>
    </div>
  );
}
